export interface ContactErrorMap { email?: string; phone?: string; general?: string }

type CodedPayload = { code?: string; message?: string; data?: Record<string, unknown> } | null;

/** Loose structural view of the many backend error envelopes we probe. */
interface ErrorLike {
  error?: unknown;
  message?: string;
}

function tryParseJson(s: unknown): unknown {
  if (typeof s !== 'string') return null;
  const t = s.trim();
  if (!t.startsWith('{') && !t.startsWith('[')) return null;
  try { return JSON.parse(t); } catch { return null; }
}

function isCoded(v: unknown): v is { code?: string; message?: string; data?: Record<string, unknown> } {
  return typeof v === 'object' && v !== null && 'code' in v;
}

/** Extract {code,message,data} from many possible backend shapes. */
function extractCodedPayload(err: unknown): CodedPayload {
  const e = err as ErrorLike | null | undefined;

  // Common HTTP error shapes: { error: <payload|string>, message?, status? }
  const root: unknown = e?.error ?? err ?? null;

  // Case A: already coded object {code,message,data}
  if (isCoded(root)) {
    return { code: root.code, message: root.message, data: root.data };
  }

  // Case B: Spring envelope { error: "BAD_REQUEST", message: "<JSON string>", ... }
  const msgMaybeJson = (root as ErrorLike | null)?.message ?? e?.message ?? null;
  const parsedFromMessage = tryParseJson(msgMaybeJson);
  if (isCoded(parsedFromMessage)) {
    return {
      code: parsedFromMessage.code,
      message: parsedFromMessage.message,
      data: parsedFromMessage.data,
    };
  }

  // Case C: sometimes the whole `error` is a JSON string
  const parsedFromError = tryParseJson(e?.error);
  if (isCoded(parsedFromError)) {
    return {
      code: parsedFromError.code,
      message: parsedFromError.message,
      data: parsedFromError.data,
    };
  }

  // Case D: final attempt, raw string that is JSON
  const parsedRaw = tryParseJson(err);
  if (isCoded(parsedRaw)) {
    return {
      code: parsedRaw.code,
      message: parsedRaw.message,
      data: parsedRaw.data,
    };
  }

  return null;
}

/** Coded → localized messages via translate(key, params). */
export function mapContactUpdateErrorByCode(
  err: unknown,
  t: (key: string, params?: Record<string, unknown>) => string
): ContactErrorMap {
  const out: ContactErrorMap = {};
  const coded = extractCodedPayload(err);

  if (coded?.code) {
    const { code, data } = coded;
    switch (code) {
      case 'PHONE_NULL':
        out.phone = t('USER_FEEDBACK.phone_null');
        return out;

      case 'PHONE_UNSUPPORTED':
        out.phone = t('USER_FEEDBACK.phone_unsupported', { number: data?.number ?? '' });
        return out;

      case 'PHONE_AMBIGUOUS': {
        const regions =
          Array.isArray(data?.regions)
            ? data.regions.join(', ')
            : typeof data?.regions === 'string'
              ? data.regions
              : '';
        out.phone = t('USER_FEEDBACK.phone_ambiguous', {
          number: data?.number ?? '',
          regions,
        });
        return out;
      }

      case 'PHONE_INVALID':
        out.phone = t('USER_FEEDBACK.phone_invalid', { number: data?.number ?? '' });
        return out;

      case 'EMAIL_INVALID':
        out.email = t('USER_FEEDBACK.email_invalid');
        return out;
    }
  }

  // Fallbacks (legacy / unknown)
  const e = err as ErrorLike | null | undefined;
  const rawMessage =
    coded?.message ??
    (e?.error as ErrorLike | null | undefined)?.message ??
    e?.message ??
    (typeof err === 'string' ? err : '');

  if (/invalid email/i.test(rawMessage)) {
    out.email = t('USER_FEEDBACK.error_occurred');
  } else if (/invalid phone/i.test(rawMessage)) {
    out.phone = t('USER_FEEDBACK.error_occurred');
  } else {
    out.general = rawMessage || t('USER_FEEDBACK.error_occurred');
  }
  return out;
}
